import { Brain, Check, X, AlertCircle, FileText, ChevronDown, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const QuizViewer = ({ content, onComplete, isCompleted, previousResponse, gradingType = 'auto', taskStatus, lastSubmissionCancelled, onRequestRetake, allowRetakes, isAdmin = false, children }) => {

    const [quiz, setQuiz] = useState(() => {
        try {
            if (typeof content === 'object' && content !== null) {
                return content;
            }
            return JSON.parse(content);
        } catch (e) {
            console.error("Error parsing quiz content:", e);
            return { questions: [] };
        }
    });

    const [answers, setAnswers] = useState(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                return parsed.answers || {};
            } catch (e) {
                console.warn("Error parsing previousResponse:", e);
                return {};
            }
        }
        return {};
    });

    const [submitted, setSubmitted] = useState(isCompleted || taskStatus === 'completed' || taskStatus === 'retake_requested' || (isAdmin && previousResponse) || false);
    const [showResults, setShowResults] = useState((isCompleted || taskStatus === 'completed' || false) || (isAdmin && previousResponse));
    const isManualGrading = gradingType === 'manual';

    // Collapsible Logic for Admin View
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());

    const toggleQuestion = (index) => {
        setExpandedQuestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    useEffect(() => {
        if (lastSubmissionCancelled) {
            setSubmitted(false);
            setShowResults(false);
        }
    }, [lastSubmissionCancelled]);

    useEffect(() => {
        if (taskStatus === 'completed' || isCompleted) {
            setSubmitted(true);
            setShowResults(true);
        } else if (taskStatus === 'retake_requested') {
            setSubmitted(true);
            setShowResults(false);
        } else if (taskStatus === 'viewed' || !taskStatus) {
            // Reset for fresh start (Retake Approved)
            setSubmitted(false);
            setShowResults(false);
        }
    }, [taskStatus, isCompleted, allowRetakes, onRequestRetake]);

    // Sync answers with previousResponse prop changes
    useEffect(() => {
        if (previousResponse) {
            try {
                const parsed = typeof previousResponse === 'string' ? JSON.parse(previousResponse) : previousResponse;
                setAnswers(parsed.answers || {});
            } catch (e) {
                setAnswers({});
            }
        } else {
            // Effectively clear answers when previousResponse is null (Retake approved)
            setAnswers({});
        }
    }, [previousResponse]);

    const handleAnswerChange = (questionIndex, value) => {
        if (!submitted) {
            setAnswers(prev => ({
                ...prev,
                [questionIndex]: value
            }));
        }
    };

    const calculateScore = () => {
        let correct = 0;
        let totalPoints = 0;
        let earnedPoints = 0;

        quiz.questions.forEach((question, index) => {
            const points = question.points || 1;
            totalPoints += points;

            if (answers[index] === question.correctAnswer) {
                correct++;
                earnedPoints += points;
            }
        });

        const percentage = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;

        return {
            correct,
            total: quiz.questions.length,
            percentage,
            earnedPoints,
            totalPoints
        };
    };

    // Memoize results definition
    const results = React.useMemo(() => {
        if (submitted && !isManualGrading) {
            return calculateScore();
        }
        return null;
    }, [submitted, isManualGrading, answers, quiz]);

    const handleSubmit = () => {
        const answeredCount = Object.keys(answers).length;
        if (answeredCount < quiz.questions.length) {
            if (!window.confirm(`You have ${quiz.questions.length - answeredCount} unanswered questions. Submit anyway?`)) {
                return;
            }
        }

        // setSubmitted(true) is handled by parent passing taskStatus='completed' after API success
        // This allows parent to validate proof/signature first

        const submissionResults = isManualGrading ? null : calculateScore();
        const responseData = {
            answers,
            results: submissionResults,
            gradingType,
            timestamp: new Date().toISOString()
        };

        onComplete(JSON.stringify(responseData), submissionResults ? submissionResults.percentage : null);
    };

    if (!quiz.questions || quiz.questions.length === 0) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-yellow-800">No quiz questions available</p>
            </div>
        );
    }



    return (
        <div className="space-y-8">
            {/* Header / Summary Section when Submitted - Show for Admin if submitted */}
            {submitted && results && (!isAdmin || (isAdmin && previousResponse)) && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-top-4">
                    <div className={`p-6 ${results.percentage >= 70 ? 'bg-green-50' : 'bg-red-50'} border-b ${results.percentage >= 70 ? 'border-green-100' : 'border-red-100'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-xl font-bold ${results.percentage >= 70 ? 'text-green-800' : 'text-red-800'}`}>
                                Quiz Results
                            </h3>
                            <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${results.percentage >= 70 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {results.percentage >= 70 ? 'Passed' : 'Failed'}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Score</p>
                                <p className={`text-3xl font-bold ${results.percentage >= 70 ? 'text-green-700' : 'text-red-700'}`}>
                                    {results.percentage}%
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Correct</p>
                                <p className="text-3xl font-bold text-gray-800">{results.correct}<span className="text-lg text-gray-400 font-normal">/{results.total}</span></p>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Points</p>
                                <p className="text-3xl font-bold text-gray-800">
                                    {results.earnedPoints}<span className="text-lg text-gray-400 font-normal">/{results.totalPoints}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Detailed Breakdown Legend */}
                    <div className="bg-gray-50 p-4 flex flex-wrap gap-4 text-sm border-t border-gray-200">
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-gray-600">Correct Answer</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <X className="w-4 h-4 text-red-600" />
                            <span className="text-gray-600">Incorrect Selection</span>
                        </div>
                    </div>
                </div>
            )}

            {!submitted && (
                /* Quiz Header - Only show if taking the quiz */
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-primary">Assessment in Progress</h3>
                    </div>
                    <div className="flex gap-4 text-sm font-medium text-primary">
                        <span>{quiz.questions.length} Questions</span>
                        <span>•</span>
                        <span>{quiz.questions.reduce((sum, q) => sum + (q.points || 1), 0)} Total Points</span>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {quiz.questions.map((question, qIndex) => {
                    // For review mode logic
                    // Ensure robust index access
                    const hasAnswer = Object.prototype.hasOwnProperty.call(answers, qIndex);
                    const userAnswerIndex = hasAnswer ? answers[qIndex] : undefined;

                    const isCorrect = !isManualGrading && userAnswerIndex === question.correctAnswer;
                    const isExpanded = !isAdmin || expandedQuestions.has(qIndex);

                    // Container styles
                    let containerClass = "bg-white border rounded-xl overflow-hidden transition-all ";
                    if (submitted && !isManualGrading && !isAdmin) {
                        if (isCorrect) {
                            containerClass += "border-green-200 ring-1 ring-green-100";
                        } else if (hasAnswer) {
                            containerClass += "border-red-200 ring-1 ring-red-100";
                        } else {
                            containerClass += "border-gray-200";
                        }
                    } else {
                        containerClass += "border-gray-200 hover:border-gray-300";
                    }

                    return (
                        <div key={qIndex} className={containerClass}>
                            {/* Question Header - Clickable for Admin */}
                            <div
                                className={`${isAdmin ? 'p-3' : 'p-6'} flex items-start gap-4 ${isAdmin ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                onClick={() => isAdmin && toggleQuestion(qIndex)}
                            >
                                {isAdmin && (
                                    <div className="mt-0.5 text-gray-400">
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className={`flex justify-between items-start gap-4 ${isAdmin ? 'mb-0' : 'mb-4'}`}>
                                        <h4 className={`font-semibold ${isAdmin ? 'text-sm' : 'text-lg'} text-gray-900 flex-1 leading-relaxed`}>
                                            <span className="text-gray-400 font-medium mr-3 select-none">{qIndex + 1}.</span>
                                            {question.question}
                                        </h4>
                                        {/* Points Badge */}
                                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md whitespace-nowrap">
                                            {question.points || 1} Points
                                        </span>
                                    </div>
                                    {isCompleted && !isAdmin && (
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {isCorrect ? 'Correct' : 'Incorrect'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Options Body - Collapsible */}
                            {isExpanded && (
                                <div className={isAdmin ? "px-4 pb-3 pt-0 pl-12" : "px-6 pb-6 pt-0 pl-16"}>
                                    <div className={isAdmin ? "space-y-2" : "space-y-3"}>
                                        {(isManualGrading || !question.options || question.options.length === 0) ? (
                                            <textarea
                                                value={userAnswerIndex || ''}
                                                onChange={(e) => handleAnswerChange(qIndex, e.target.value)}
                                                disabled={submitted || isAdmin}
                                                placeholder="Type your answer here..."
                                                rows="4"
                                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
                                            />
                                        ) : (
                                            question.options.map((option, oIndex) => {
                                                const isSelected = userAnswerIndex === oIndex;
                                                const isCorrectOption = oIndex === question.correctAnswer;

                                                // Option Styling
                                                let optionClass = isAdmin
                                                    ? "border rounded-md p-2 flex items-start gap-2 transition-all "
                                                    : "border-2 rounded-lg p-4 flex items-start gap-3 transition-all ";

                                                if (!submitted && !isAdmin) {
                                                    // Active State
                                                    optionClass += "cursor-pointer ";
                                                    if (isSelected) {
                                                        optionClass += "border-primary bg-primary/5";
                                                    } else {
                                                        optionClass += "border-gray-200 hover:border-gray-300 hover:bg-gray-50";
                                                    }
                                                } else {
                                                    // Submitted / Review State / Admin View
                                                    if (!isManualGrading) {
                                                        if (isCorrectOption && (submitted || isAdmin)) {
                                                            optionClass += "border-green-500 bg-green-50 shadow-sm";
                                                        } else if (isSelected && !isCorrectOption) {
                                                            optionClass += "border-red-500 bg-red-50 shadow-sm";
                                                        } else {
                                                            optionClass += "border-gray-100 opacity-60";
                                                        }
                                                    } else {
                                                        // Manual Grading Submitted
                                                        if (isSelected) {
                                                            optionClass += "border-gray-400 bg-gray-100";
                                                        } else {
                                                            optionClass += "border-gray-100 opacity-60";
                                                        }
                                                    }
                                                }

                                                return (
                                                    <label key={oIndex} className={optionClass}>
                                                        <input
                                                            type="radio"
                                                            name={`question-${qIndex}`}
                                                            checked={isSelected}
                                                            onChange={() => handleAnswerChange(qIndex, oIndex)}
                                                            disabled={submitted || isAdmin}
                                                            className={`accent-primary ${isAdmin ? 'w-3 h-3 mt-1' : 'w-4 h-4 mt-1'}`}
                                                        />
                                                        <div className="flex-1">
                                                            <span className={`text-base ${(isSelected || (isAdmin && isCorrectOption)) ? 'font-medium text-gray-900' : 'text-gray-700'} ${isAdmin ? 'text-sm' : 'text-base'}`}>
                                                                {option}
                                                            </span>

                                                            {/* Explicit Status Labels for Review Mode */}
                                                            {(submitted || isAdmin) && !isManualGrading && (
                                                                <div className="mt-1">
                                                                    {isCorrectOption && (
                                                                        <span className="text-xs font-bold text-green-700 flex items-center gap-1 mt-1">
                                                                            <Check className="w-3 h-3" /> Correct Answer
                                                                        </span>
                                                                    )}
                                                                    {isSelected && !isCorrectOption && (
                                                                        <span className="text-xs font-bold text-red-700 flex items-center gap-1 mt-1">
                                                                            <X className="w-3 h-3" /> Your Answer (Incorrect)
                                                                        </span>
                                                                    )}
                                                                    {isSelected && isCorrectOption && (
                                                                        <span className="text-xs font-bold text-green-700 flex items-center gap-1 mt-1">
                                                                            <Check className="w-3 h-3" /> Your Answer (Correct)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Explanation - Only for auto-graded */}
                                    {(submitted || isAdmin) && !isManualGrading && question.explanation && (
                                        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-4">
                                            <div className="bg-blue-100/50 p-2 rounded-lg h-fit text-blue-600">
                                                <Brain className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h5 className="font-bold text-blue-900 text-sm mb-1 uppercase tracking-wide">Explanation</h5>
                                                <p className="text-sm text-blue-800 leading-relaxed">
                                                    {question.explanation}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Question Status Footer - Hide for Admin */}
                            {submitted && !isManualGrading && !isAdmin && (
                                <div className={`px-6 py-3 border-t text-sm font-bold flex justify-between items-center ${isCorrect ? 'bg-green-50 border-green-100 text-green-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
                                    <span className="flex items-center gap-2">
                                        {isCorrect ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                        Result: {isCorrect ? 'Correct' : 'Incorrect'}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Inject Children (Proof/Signature) before Submit Actions */}
            {!submitted && !isAdmin && children}

            {/* Submit/Results Footer actions - Hide for Admin */}
            {!isAdmin && (
                <div className="flex justify-end pt-4">
                    {!submitted ? (
                        <button
                            onClick={handleSubmit}
                            className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-8 py-4 rounded-xl font-bold shadow-lg shadow-primary/20 transition-all hover:scale-105"
                        >
                            <Check className="w-5 h-5" />
                            {isManualGrading ? 'Submit for Review' : 'Submit Quiz'}
                        </button>
                    ) : (
                        <div className="flex gap-3">
                            {allowRetakes && onRequestRetake && (
                                <button
                                    onClick={onRequestRetake}
                                    className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-900 px-6 py-3 rounded-xl font-bold transition-all"
                                >
                                    <AlertCircle className="w-5 h-5" />
                                    Request Retake
                                </button>
                            )}
                            {/* Status Badge */}
                            <div className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-500 rounded-xl font-medium cursor-default">
                                {isManualGrading ? <FileText className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                                {isManualGrading ? 'Submitted for Review' : 'Completed'}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuizViewer;

